// Registry editor (content authoring). Client-only (ENG-1).
//
// One screen edits items, entities and blocks, because it does not know what any
// of them look like: it reads the zod schemas through `introspect` and builds the
// form from that. A bounded number gets a slider, an enum a dropdown, a boolean a
// checkbox, a string array a comma list. Add a field to schemas.ts and it appears
// here with no change to this file.
//
// Edits are stored as a sparse overlay (only the fields you actually changed) and
// applied to the registries at load, so they are live everywhere and export as an
// archive that merges into a versioned collection.
import { h, clear } from '../h';
import { describeSchema, isSlider, type FieldDesc } from '../../../sim/registry/introspect';
import { itemSchema, mobSchema, blockSchema } from '../../../sim/registry/schemas';
import {
  clearEntry, isEmptyOverlay, overlaySize, setField, UNSET, type Overlay, type OverlayKind,
} from '../../../sim/registry/overlay';
import { loadOverlay, saveOverlay, clearOverlay } from '../../storage/overlay';
import {
  ICON_SIZE, FACE_NAMES, blankIcon, iconFromRgba, iconToRgba, setPixel, isValidIcon, rgbToHex,
  type IconData, type BlockTexture, type FaceName,
} from '../../../sim/registry/icon';
import type { MobDef } from '../../../sim/registry/schemas';
import type { Game } from '../../boot';

interface Tab {
  kind: OverlayKind;
  label: string;
  fields: FieldDesc[];
  /** Live entries, by name, as the registry currently holds them. */
  entries(game: Game): Map<string, Record<string, unknown>>;
}

const TABS: Tab[] = [
  {
    kind: 'items', label: 'Items', fields: describeSchema(itemSchema),
    entries: (game) => new Map([...game.registries.items.byName].map(([n, d]) => [n, d as unknown as Record<string, unknown>])),
  },
  {
    kind: 'mobs', label: 'Entities', fields: describeSchema(mobSchema),
    entries: (game) => new Map([...game.registries.mobs.byName].map(([n, d]) => [n, d as unknown as Record<string, unknown>])),
  },
  {
    kind: 'blocks', label: 'Blocks', fields: describeSchema(blockSchema),
    entries: (game) => new Map([...game.registries.blocks.byName].map(([n, d]) => [n, d as unknown as Record<string, unknown>])),
  },
];

/** A solid 16×16 block of one colour — the starting point when painting a rig
 *  part that has only ever been a flat colour. */
function filledIcon(hex: string): IconData {
  return { palette: [hex.toLowerCase()], pixels: '0'.repeat(ICON_SIZE * ICON_SIZE) };
}

/** A rig part without its painted skin (back to the flat colour). */
function omitTexture(part: Record<string, unknown>): Record<string, unknown> {
  const { texture: _drop, ...rest } = part;
  return rest;
}

const pretty = (id: string): string => id.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

/** Fields the texture panel owns — showing them again as raw JSON above it is
 *  just a second, worse editor for the same data. */
const TEXTURE_FIELDS: Partial<Record<OverlayKind, string>> = { items: 'icon', mobs: 'rig', blocks: 'texture' };

export function showEditor(game: Game, onBack: () => void): HTMLElement {
  const root = h('div', { class: 'bb-screen bb-editor' });
  document.body.append(root);

  let overlay: Overlay = loadOverlay();
  let tab = TABS[0]!;
  let selected: string | null = null;
  let query = '';

  const dirtyCount = h('span', { class: 'bb-editor-count' });
  const list = h('div', { class: 'bb-editor-list' });
  const form = h('div', { class: 'bb-editor-form' });

  const persist = (): void => {
    saveOverlay(overlay);
    syncCount();
    renderList(); // modified markers move as you edit
  };
  const syncCount = (): void => {
    const n = overlaySize(overlay);
    dirtyCount.textContent = n === 0 ? 'no changes' : `${n} edited — reload to apply`;
  };

  /** The value in force for a field: the overlay's, else the shipped one. */
  const valueOf = (name: string, entry: Record<string, unknown>, field: string): unknown => {
    const patch = overlay.patches[tab.kind]?.[name];
    if (patch && field in patch) return patch[field] === UNSET ? undefined : patch[field];
    return entry[field];
  };
  const isEdited = (name: string): boolean => Boolean(overlay.patches[tab.kind]?.[name]);

  // ── Field controls, chosen from the schema description ────────────────────
  function control(name: string, entry: Record<string, unknown>, f: FieldDesc): HTMLElement | null {
    const shipped = entry[f.key];
    const current = valueOf(name, entry, f.key);
    const commit = (v: unknown): void => {
      overlay = setField(overlay, tab.kind, name, f.key, v, shipped);
      persist();
    };

    if (f.kind === 'enum') {
      const sel = h('select', { class: 'bb-select' });
      if (f.optional) sel.append(h('option', { value: '' }, '— unset —'));
      for (const v of f.values ?? []) sel.append(h('option', { value: v }, v));
      sel.value = current === undefined ? '' : String(current);
      sel.onchange = () => commit(sel.value === '' ? UNSET : sel.value);
      return sel;
    }

    if (f.kind === 'boolean') {
      const box = h('input', { class: 'bb-check', type: 'checkbox' });
      box.checked = Boolean(current ?? f.fallback);
      box.onchange = () => commit(box.checked);
      return box;
    }

    if (f.kind === 'number') {
      if (isSlider(f)) {
        const step = f.int ? 1 : 0.1;
        const slider = h('input', {
          class: 'bb-slider', type: 'range',
          min: String(f.min), max: String(f.max), step: String(step),
        });
        const read = h('span', { class: 'bb-editor-value' });
        const show = (v: number): void => { read.textContent = String(v); };
        const start = Number(current ?? f.fallback ?? f.min ?? 0);
        slider.value = String(start); show(start);
        slider.oninput = () => show(Number(slider.value));
        slider.onchange = () => commit(f.int ? Math.round(Number(slider.value)) : Number(slider.value));
        return h('div', { class: 'bb-editor-slider' }, slider, read);
      }
      const num = h('input', { class: 'bb-input', type: 'number', step: f.int ? '1' : 'any' });
      num.value = current === undefined ? '' : String(current);
      num.onchange = () => commit(num.value === '' ? UNSET : Number(num.value));
      return num;
    }

    if (f.kind === 'array' && f.element?.kind === 'string') {
      const text = h('input', { class: 'bb-input', type: 'text', placeholder: 'comma, separated' });
      text.value = Array.isArray(current) ? (current as string[]).join(', ') : '';
      text.onchange = () => {
        const parts = text.value.split(',').map((s) => s.trim()).filter(Boolean);
        commit(parts);
      };
      return text;
    }

    if (f.kind === 'string') {
      const text = h('input', { class: 'bb-input', type: 'text' });
      text.value = current === undefined ? '' : String(current);
      text.disabled = f.key === 'name'; // renaming would orphan every reference
      text.onchange = () => commit(text.value === '' ? UNSET : text.value);
      return text;
    }

    // Nested objects / unions: edit as JSON rather than pretend to understand it.
    const area = h('textarea', { class: 'bb-input bb-editor-json', rows: '3' });
    area.value = current === undefined ? '' : JSON.stringify(current);
    area.onchange = () => {
      if (area.value.trim() === '') { commit(UNSET); return; }
      try {
        commit(JSON.parse(area.value));
        area.classList.remove('bb-editor-bad');
      } catch {
        area.classList.add('bb-editor-bad'); // keep the text so it can be fixed
      }
    };
    return area;
  }

  /** Texture editing. Two different things wear the same name here:
   *   • an item's look is a 16×16 icon — procedural in code, so editing means
   *     writing a data-driven `icon` override (seeded from the current sprite so
   *     you start from what the item already looks like);
   *   • a mob's look IS its `rig` — coloured boxes that are already data, so
   *     editing means colour pickers per body part.
   *  Both land in the overlay as ordinary fields, so export/import/collections
   *  carry them with no extra plumbing. */
  function renderTexture(name: string, entry: Record<string, unknown>): HTMLElement | null {
    if (tab.kind === 'items') return renderIconEditor(name, entry);
    if (tab.kind === 'mobs') return renderRigEditor(name, entry);
    if (tab.kind === 'blocks') return renderBlockTexture(name, entry);
    return null;
  }

  /** Current icon for an item: the override if one exists, else the rendered
   *  sprite read back off the icon canvas and quantised. */
  function currentIcon(name: string, entry: Record<string, unknown>): IconData {
    const override = valueOf(name, entry, 'icon') as IconData | undefined;
    if (override && isValidIcon(override)) return override;
    const id = game.registries.items.idOf.get(name);
    if (id === undefined) return blankIcon();
    try {
      // Read the already-rendered canvas directly. Going via the data URL and an
      // Image would decode asynchronously and hand back a blank square.
      const src = game.icons.canvas(id);
      const ctx = src.getContext('2d', { willReadFrequently: true });
      if (!ctx) return blankIcon();
      return iconFromRgba(ctx.getImageData(0, 0, ICON_SIZE, ICON_SIZE).data);
    } catch {
      return blankIcon();
    }
  }

  /** The shared 16×16 painter. Items and mob parts both use it; the caller says
   *  what the starting pixels are and what to do when a stroke finishes. */
  function pixelEditor(
    initial: IconData,
    onCommit: (icon: IconData) => void,
    onReset?: () => void,
  ): HTMLElement {
    const PX = 16; // on-screen size of one texel
    let icon = initial;
    let color = icon.palette[0] ?? '#c83232';
    let erasing = false;

    const cvs = h('canvas', { class: 'bb-tex-canvas', 'data-testid': 'icon-canvas' }) as HTMLCanvasElement;
    cvs.width = ICON_SIZE * PX; cvs.height = ICON_SIZE * PX;
    const ctx = cvs.getContext('2d')!;
    const palette = h('div', { class: 'bb-tex-palette' });

    const paintPalette = (): void => {
      clear(palette);
      for (const hex of icon.palette) {
        const dot = h('button', { class: 'bb-tex-swatch', style: { background: hex }, title: hex });
        dot.onclick = () => { color = hex; swatch.value = hex; erasing = false; eraser.classList.remove('bb-filter-on'); };
        palette.append(dot);
      }
    };
    const paint = (): void => {
      ctx.clearRect(0, 0, cvs.width, cvs.height);
      const rgba = iconToRgba(icon);
      for (let y = 0; y < ICON_SIZE; y++) {
        for (let x = 0; x < ICON_SIZE; x++) {
          const p = (y * ICON_SIZE + x) * 4;
          ctx.fillStyle = (x + y) % 2 ? '#3a4152' : '#333a49'; // transparency check
          ctx.fillRect(x * PX, y * PX, PX, PX);
          if ((rgba[p + 3] ?? 0) > 0) {
            ctx.fillStyle = rgbToHex(rgba[p] ?? 0, rgba[p + 1] ?? 0, rgba[p + 2] ?? 0);
            ctx.fillRect(x * PX, y * PX, PX, PX);
          }
        }
      }
    };
    const at = (ev: MouseEvent): { x: number; y: number } => {
      const r = cvs.getBoundingClientRect();
      return {
        x: Math.floor(((ev.clientX - r.left) / r.width) * ICON_SIZE),
        y: Math.floor(((ev.clientY - r.top) / r.height) * ICON_SIZE),
      };
    };
    let drawing = false, dirty = false;
    const stroke = (ev: MouseEvent): void => {
      const { x, y } = at(ev);
      const next = setPixel(icon, x, y, erasing ? null : color);
      if (next === icon) return;
      icon = next;
      dirty = true;
      paint();
    };
    cvs.addEventListener('mousedown', (e) => { drawing = true; stroke(e); });
    cvs.addEventListener('mousemove', (e) => { if (drawing) stroke(e); });
    const endStroke = (): void => {
      if (!drawing) return;
      drawing = false;
      if (!dirty) return;
      dirty = false;
      onCommit(icon);
      paintPalette(); // a new colour may have joined the palette
    };
    cvs.addEventListener('mouseup', endStroke);
    cvs.addEventListener('mouseleave', endStroke);

    const swatch = h('input', { class: 'bb-tex-color', type: 'color', value: color, 'data-testid': 'icon-color' }) as HTMLInputElement;
    swatch.oninput = () => { color = swatch.value; erasing = false; eraser.classList.remove('bb-filter-on'); };
    const eraser = h('button', { class: 'bb-btn bb-tex-btn', 'data-testid': 'icon-eraser' }, 'Erase');
    eraser.onclick = () => { erasing = !erasing; eraser.classList.toggle('bb-filter-on', erasing); };

    const tools = h('div', { class: 'bb-tex-tools' }, swatch, eraser);
    if (onReset) {
      const reset = h('button', { class: 'bb-btn bb-danger bb-tex-btn' }, 'Reset texture');
      reset.onclick = () => onReset();
      tools.append(reset);
    }

    paint();
    paintPalette();
    return h('div', null, cvs, tools, palette,
      h('div', { class: 'bb-muted' }, 'Click or drag to paint. Pick a colour, or click a swatch to reuse one.'));
  }

  function renderIconEditor(name: string, entry: Record<string, unknown>): HTMLElement {
    // Invalidating the cache alone was not enough: the repaint re-read the item
    // def from the registry loaded at boot, so the edit only appeared after a
    // reload. The override goes into the icon cache itself.
    const commit = (icon: IconData): void => {
      overlay = setField(overlay, 'items', name, 'icon', icon, entry.icon);
      persist();
      game.reskinItem(name, icon);
    };
    const reset = (): void => {
      overlay = setField(overlay, 'items', name, 'icon', UNSET, entry.icon);
      persist();
      game.reskinItem(name, null);
      renderForm();
    };
    return h('div', { class: 'bb-tex' },
      h('div', { class: 'bb-editor-h' }, 'Texture'),
      pixelEditor(currentIcon(name, entry), commit, reset));
  }

  /** A block's WORLD texture is its atlas tile, not its item icon — editing it
   *  here repaints the atlas live, so placed blocks change without a reload. */
  /** Seed a face's editor from the atlas tile it actually draws with. */
  function iconOfTile(tile: number): IconData {
    try {
      if (tile < 0) return blankIcon();
      const ctx = game.atlasCanvas.getContext('2d', { willReadFrequently: true });
      if (!ctx) return blankIcon();
      return iconFromRgba(ctx.getImageData(tile * ICON_SIZE, 0, ICON_SIZE, ICON_SIZE).data);
    } catch {
      return blankIcon();
    }
  }

  /** The atlas tile each of the block's six faces uses. */
  function faceTiles(name: string): number[] {
    const state = game.registries.states.defaultOf.get(name);
    if (state === undefined) return [];
    return Array.from({ length: 6 }, (_, f) => game.renderTable.tiles[state * 6 + f] ?? -1);
  }

  /** A block's world texture lives in the atlas, not in its item icon. Faces
   *  frequently SHARE one tile (stone uses a single tile for all six), and
   *  repainting a shared tile necessarily changes every face on it — so group
   *  the faces by tile and offer one painter per group rather than six painters
   *  that would silently overwrite each other. */
  function renderBlockTexture(name: string, entry: Record<string, unknown>): HTMLElement {
    const wrap = h('div', { class: 'bb-tex' }, h('div', { class: 'bb-editor-h' }, 'Texture — faces'));
    const tiles = faceTiles(name);
    if (tiles.length === 0 || tiles.every((x) => x < 0)) {
      wrap.append(h('div', { class: 'bb-muted' }, 'This block has no atlas tiles to paint.'));
      return wrap;
    }
    // Read fresh every time. Capturing this once meant each face painted from a
    // stale snapshot, so painting a second face silently dropped the first.
    const readTex = (): BlockTexture => (valueOf(name, entry, 'texture') ?? {}) as BlockTexture;

    // tile → the faces drawn with it, in face order.
    const groups = new Map<number, FaceName[]>();
    tiles.forEach((tile, f) => {
      if (tile < 0) return;
      const list = groups.get(tile) ?? [];
      list.push(FACE_NAMES[f]!);
      groups.set(tile, list);
    });

    const painter = h('div', { class: 'bb-tex-part' });
    const writeTexture = (next: BlockTexture): void => {
      const value = Object.keys(next).length === 0 ? UNSET : next;
      overlay = setField(overlay, 'blocks', name, 'texture', value, entry.texture);
      persist();
      game.reskinBlock(name, value === UNSET ? null : next);
    };

    const openGroup = (tile: number, faces: FaceName[]): void => {
      clear(painter);
      const label = faces.length === 6 ? 'All faces' : faces.join(' / ');
      const tex0 = readTex();
      const seed = tex0[faces[0]!] ?? tex0.all ?? iconOfTile(tile);
      painter.append(
        h('div', { class: 'bb-editor-h' }, `Painting: ${label}`),
        pixelEditor(seed,
          (icon) => {
            const next: BlockTexture = { ...readTex() };
            for (const f of faces) next[f] = icon;
            delete next.all; // per-face entries supersede the blanket one
            writeTexture(next);
          },
          () => {
            const next: BlockTexture = { ...readTex() };
            for (const f of faces) delete next[f];
            writeTexture(next);
            renderForm();
          }));
    };

    const shown = readTex();
    for (const [tile, faces] of groups) {
      const label = faces.length === 6 ? 'All faces' : faces.join(' / ');
      const painted = faces.some((f) => shown[f]) || Boolean(shown.all);
      const btn = h('button', { class: 'bb-btn bb-tex-btn', 'data-face-tile': String(tile) },
        `${painted ? 'Edit' : 'Paint'} ${label}`);
      btn.onclick = () => openGroup(tile, faces);
      wrap.append(h('div', { class: 'bb-editor-row' },
        h('label', { class: 'bb-editor-label' }, label),
        h('div', { class: 'bb-tex-tools' }, btn)));
    }
    wrap.append(painter);
    if (groups.size === 1) {
      wrap.append(h('div', { class: 'bb-muted' }, 'Every face of this block shares one tile, so painting changes all of them.'));
    }
    return wrap;
  }

  /** A mob's look is its rig: a box per part. Each box has six sides, so a part
   *  offers the same face choices a block does — "All sides" plus each side —
   *  rather than one skin smeared over the whole box. */
  function renderRigEditor(name: string, entry: Record<string, unknown>): HTMLElement | null {
    // Same rule as blocks: every write must start from the CURRENT rig, or
    // painting one side wipes the side painted before it.
    const readRig = (): Array<Record<string, unknown>> =>
      (valueOf(name, entry, 'rig') ?? []) as Array<Record<string, unknown>>;
    const rig = readRig();
    if (!Array.isArray(rig) || rig.length === 0) return null;
    const wrap = h('div', { class: 'bb-tex' }, h('div', { class: 'bb-editor-h' }, 'Texture — body parts'));
    const painter = h('div', { class: 'bb-tex-part' });

    const writeRig = (next: Array<Record<string, unknown>>): void => {
      overlay = setField(overlay, 'mobs', name, 'rig', next, entry.rig);
      persist();
      game.reskinMob(name, next as unknown as MobDef['rig']);
    };

    const openSide = (i: number, side: 'all' | FaceName): void => {
      clear(painter);
      const part = readRig()[i]!;
      const tex = (part.texture ?? {}) as BlockTexture;
      const hex = typeof part.color === 'string' ? part.color : '#ffffff';
      // An unpainted side starts as a solid block of the part's own colour, so
      // painting edits what you already see.
      const seed = tex[side] ?? tex.all ?? filledIcon(hex);
      const label = `${String(part.name ?? `part ${i}`)} — ${side === 'all' ? 'all sides' : side}`;
      painter.append(
        h('div', { class: 'bb-editor-h' }, `Painting: ${label}`),
        pixelEditor(seed,
          (icon) => {
            const live = readRig();
            const cur = (live[i]?.texture ?? {}) as BlockTexture;
            const next: BlockTexture = { ...cur, [side]: icon };
            if (side === 'all') for (const f of FACE_NAMES) delete next[f];
            writeRig(live.map((p, j) => (j === i ? { ...p, texture: next } : p)));
          },
          () => {
            const live = readRig();
            const next: BlockTexture = { ...((live[i]?.texture ?? {}) as BlockTexture) };
            delete next[side];
            const cleared = Object.keys(next).length === 0;
            writeRig(live.map((p, j) => (j === i ? (cleared ? omitTexture(p) : { ...p, texture: next }) : p)));
            renderForm();
          }));
    };

    rig.forEach((part, i) => {
      const hex = typeof part.color === 'string' ? part.color : '#ffffff';
      const tex = (part.texture ?? {}) as BlockTexture;
      const input = h('input', { class: 'bb-tex-color', type: 'color', value: hex, 'data-rig': String(i) }) as HTMLInputElement;
      input.oninput = () => writeRig(readRig().map((p, j) => (j === i ? { ...p, color: input.value } : p)));

      const sides = h('div', { class: 'bb-tex-tools' }, input);
      for (const side of ['all', ...FACE_NAMES] as const) {
        const painted = Boolean(tex[side]);
        const btn = h('button', {
          class: `bb-btn bb-tex-btn${painted ? ' bb-filter-on' : ''}`,
          'data-rig-side': `${i}:${side}`,
          title: side === 'all' ? 'Paint every side of this box' : `Paint the ${side} side`,
        }, side === 'all' ? 'All' : side);
        btn.onclick = () => openSide(i, side);
        sides.append(btn);
      }
      wrap.append(h('div', { class: 'bb-editor-row bb-tex-partrow' },
        h('label', { class: 'bb-editor-label' }, String(part.name ?? `part ${i}`)), sides));
    });
    wrap.append(painter);
    return wrap;
  }

  function renderForm(): void {
    clear(form);
    if (!selected) {
      form.append(h('div', { class: 'bb-editor-empty' }, 'Pick an entry to edit.'));
      return;
    }
    const entry = tab.entries(game).get(selected);
    if (!entry) return;
    const header = h('div', { class: 'bb-editor-head' },
      h('div', { class: 'bb-editor-title' }, pretty(selected)),
      h('code', { class: 'bb-editor-id' }, selected));
    if (isEdited(selected)) {
      const revert = h('button', { class: 'bb-btn bb-danger bb-editor-revert' }, 'Revert');
      revert.onclick = () => { overlay = clearEntry(overlay, tab.kind, selected!); persist(); renderForm(); };
      header.append(revert);
    }
    form.append(header);

    for (const f of tab.fields) {
      if (TEXTURE_FIELDS[tab.kind] === f.key) continue; // rendered by the texture panel
      const ctl = control(selected, entry, f);
      if (!ctl) continue;
      const row = h('div', { class: 'bb-editor-row' },
        h('label', { class: 'bb-editor-label', title: f.description ?? '' },
          f.key, f.optional ? h('span', { class: 'bb-editor-opt' }, 'optional') : null),
        ctl);
      form.append(row);
    }

    const tex = renderTexture(selected, entry);
    if (tex) form.append(tex);
    if (tab.kind === 'mobs') form.append(renderAttach(entry));
  }

  /** §27.13's "Attach to this entity" (§27.16): give the ONE mob under the crosshair its
   *  own script instead of its type's. The name field starts at this entry's `script` as
   *  a starting point to edit; "Detach" puts the mob back on its type's script. */
  function renderAttach(entry: Record<string, unknown>): HTMLElement {
    const name = h('input', {
      class: 'bb-input', type: 'text', placeholder: 'script name',
      value: typeof entry.script === 'string' ? entry.script : '', 'data-testid': 'attach-script-name',
    }) as HTMLInputElement;
    const status = h('div', { class: 'bb-editor-value', 'data-testid': 'attach-script-status' });
    const attach = h('button', { class: 'bb-btn', 'data-testid': 'attach-script' }, 'Attach to this entity');
    attach.onclick = () => { status.textContent = game.attachScriptToTarget(name.value.trim()); };
    const detach = h('button', { class: 'bb-btn bb-danger', 'data-testid': 'detach-script' }, 'Detach');
    detach.onclick = () => { status.textContent = game.attachScriptToTarget(null); };
    return h('div', { class: 'bb-editor-row' },
      h('label', { class: 'bb-editor-label', title: 'Run a script on the one mob you are looking at, instead of its type\'s' },
        'this entity'),
      h('div', {}, name, attach, detach, status));
  }

  function renderList(): void {
    clear(list);
    const q = query.trim().toLowerCase();
    const names = [...tab.entries(game).keys()]
      .filter((n) => !q || n.includes(q))
      .sort();
    for (const name of names.slice(0, 400)) {
      const btn = h('button', {
        class: `bb-editor-item${name === selected ? ' bb-editor-sel' : ''}${isEdited(name) ? ' bb-editor-dirty' : ''}`,
        'data-entry': name,
      }, pretty(name));
      btn.onclick = () => { selected = name; renderList(); renderForm(); };
      list.append(btn);
    }
    if (names.length > 400) list.append(h('div', { class: 'bb-editor-more' }, `…${names.length - 400} more — refine the search`));
    if (names.length === 0) list.append(h('div', { class: 'bb-editor-empty' }, 'No matches.'));
  }

  // ── Chrome ────────────────────────────────────────────────────────────────
  const tabsRow = h('div', { class: 'bb-creative-tabs' });
  for (const t of TABS) {
    const btn = h('button', { class: `bb-tab${t === tab ? ' bb-tab-active' : ''}`, 'data-tab': t.kind }, t.label);
    btn.onclick = () => {
      tab = t; selected = null;
      for (const el of tabsRow.children) el.classList.toggle('bb-tab-active', el === btn);
      renderList(); renderForm();
    };
    tabsRow.append(btn);
  }

  const search = h('input', { class: 'bb-input', type: 'text', placeholder: 'Search…', 'data-testid': 'editor-search' });
  search.oninput = () => { query = search.value; renderList(); };

  const exportBtn = h('button', { class: 'bb-btn', 'data-testid': 'editor-export' }, 'Export archive');
  exportBtn.onclick = () => exportArchive(overlay, game);

  // Discarding throws away every edit across every registry, so it asks first:
  // one click arms it, a second within a few seconds goes through.
  const resetBtn = h('button', { class: 'bb-btn bb-danger', 'data-testid': 'editor-discard' }, 'Discard all');
  let armed: ReturnType<typeof setTimeout> | null = null;
  const disarm = (): void => {
    if (armed) clearTimeout(armed);
    armed = null;
    resetBtn.textContent = 'Discard all';
    resetBtn.classList.remove('bb-confirm');
  };
  resetBtn.onclick = () => {
    if (isEmptyOverlay(overlay)) return;
    if (!armed) {
      resetBtn.textContent = `Discard ${overlaySize(overlay)} edit(s)? Click again`;
      resetBtn.classList.add('bb-confirm');
      armed = setTimeout(disarm, 5000);
      return;
    }
    disarm();
    clearOverlay();
    overlay = loadOverlay();
    persist(); renderForm();
  };

  const back = h('button', { class: 'bb-btn bb-primary' }, 'Done');
  const close = (): void => { document.removeEventListener('keydown', onKey, true); root.remove(); onBack(); };
  back.onclick = close;
  const onKey = (e: KeyboardEvent): void => {
    if (e.code === 'Escape') { e.preventDefault(); e.stopPropagation(); close(); }
  };
  document.addEventListener('keydown', onKey, true);

  root.append(
    h('div', { class: 'bb-title' }, 'Content Editor'),
    tabsRow,
    h('div', { class: 'bb-row' }, search, dirtyCount),
    h('div', { class: 'bb-editor-body' }, list, form),
    h('div', { class: 'bb-row' }, exportBtn, resetBtn, back),
  );

  syncCount();
  renderList();
  renderForm();
  return root;
}

/** Write the overlay out as a downloadable archive: what the editor changed,
 *  plus the collection/version it targets, ready to drop into the repo. */
function exportArchive(overlay: Overlay, game: Game): void {
  const archive = {
    ...overlay,
    exportedAt: new Date().toISOString(),
    counts: {
      items: game.registries.items.byName.size,
      mobs: game.registries.mobs.byName.size,
      blocks: game.registries.blocks.byName.size,
    },
  };
  const blob = new Blob([JSON.stringify(archive, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${overlay.collection}-${overlay.version}.overlay.json`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
